import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  IRacunInfoDto,
  ResultOfListOfVezaniRacunDto,
  StatusRacuna,
  SubscriptionPackage,
  TipRacuna,
  VezaniRacunDto,
  VrstaRacuna,
} from '@kodit/core/data-api';
import { AuthService, LocationService, SharedService } from '@kodit/core/services';
import { BehaviorSubject, Subscription } from 'rxjs';
import { RacunInfoService } from '../racun-info/racun-info.service';
import { DynamicDialogConfig, DynamicDialogService } from '@kodit/core/shared-ui';
import { DialogSize } from 'libs/core/shared-ui/src/lib/dialogs/dynamic-dialog/dynamicdialog-config';
import { AddPovezaniRacunFormComponent } from './add-povezani-racun-form/add-povezani-racun-form.component';
import { AddEOtpremnicaFormComponent } from './add-eotpremnica-form/add-eotpremnica-form.component';
import { MenuItem } from 'primeng/api';


@Component({
  selector: 'kodit-osnovne-informacije',
  templateUrl: './osnovne-informacije.component.html',
  styleUrls: ['./osnovne-informacije.component.scss'],
})
export class OsnovneInformacijeComponent implements OnInit, OnDestroy {
  private _getSub = new Subscription();

  dialogConfig: DynamicDialogConfig = new DynamicDialogConfig(DialogSize.SMALL);

  /** Props */
  tipRacuna = TipRacuna;
  vrsta = VrstaRacuna;
  racunInfoDto: IRacunInfoDto;
  naziv: string;
  jeUlazniRacun: boolean;
  razlogLabel: string;
  showAddPovezaniRacuni: boolean;
  jeAvansniZaPovezivanje: boolean;
  jeFakturaZaPovezivanje: boolean;
  jePredracunZaPovezivanje: boolean;
  jeOtpremnicaZaPovezivanje: boolean;
  jeDokumentOSmanjenjuZaPovezivanje: boolean;
  buttonLabel: string;
  vezaniRacuniDto: VezaniRacunDto[] = [];
  showAddEOtpremnica: boolean;
  vezivanjItems: MenuItem[] = [];

  constructor(
    private _location: LocationService,
    private _sharedService: SharedService,
    private _racunInfoService: RacunInfoService,
    private _dialogService: DynamicDialogService,
    private _authService: AuthService
  ) {}

  ngOnInit(): void {
    this._getSub.add(
      this._racunInfoService.getRacunInfoDto.subscribe((res) => {
        if (res) {
          this.racunInfoDto = res;
          this.jeUlazniRacun = this.racunInfoDto.vrsta === VrstaRacuna.ULAZNI;
          this.naziv = this._sharedService.racunNames[this.racunInfoDto.tip];
          this._setRazlogText();
          this.jeAvansniZaPovezivanje = this.racunInfoDto.tip == TipRacuna.AVANSNI_RACUN && this.racunInfoDto.vrsta == VrstaRacuna.IZLAZNI;
          this.jeFakturaZaPovezivanje = this.racunInfoDto.tip == TipRacuna.IZLAZNA_FAKTURA;
          this.jeOtpremnicaZaPovezivanje =  this.racunInfoDto.tip == TipRacuna.OTPREMNICA;
          this.jePredracunZaPovezivanje = this.racunInfoDto.tip == TipRacuna.PREDRACUN;
          this.jeDokumentOSmanjenjuZaPovezivanje = this.racunInfoDto.tip == TipRacuna.DOKUMENT_O_SMANJENJU
            && this.racunInfoDto.vrsta == VrstaRacuna.IZLAZNI;

          const jeNacrt = this.racunInfoDto.statusDto.status == StatusRacuna.NACRT;
          const hasEOtpremnice = this._authService.hasPackage(
            SubscriptionPackage[SubscriptionPackage.EOTPREMNICE]
          );

          this.showAddPovezaniRacuni =
            (this.jeAvansniZaPovezivanje || this.jeFakturaZaPovezivanje || this.jeOtpremnicaZaPovezivanje || this.jePredracunZaPovezivanje)
            && jeNacrt;

          this.showAddEOtpremnica =
            (this.jeFakturaZaPovezivanje || this.jeDokumentOSmanjenjuZaPovezivanje) && jeNacrt && hasEOtpremnice;

          this.buttonLabel = this.jeAvansniZaPovezivanje ? 'Veži za predračun' :
            this.jeFakturaZaPovezivanje ? 'Veži za otpremnicu' :
            this.jeOtpremnicaZaPovezivanje ? 'Veži za fakturu' :
            'Veži za avansni račun';

          if (this.jeFakturaZaPovezivanje && jeNacrt) {
            this.vezivanjItems = [
              {
                label: 'Veži za otpremnicu',
                command: () => this.openAddPovezaniRacunDialog(),
              },
              ...(hasEOtpremnice
                ? [
                    {
                      label: 'Veži za eOtpremnicu/ePrijemnicu',
                      command: () => this.openAddEOtpremnicaDialog(),
                    },
                  ]
                : []),
            ];
          } else if (this.jeDokumentOSmanjenjuZaPovezivanje && jeNacrt && hasEOtpremnice) {
            this.vezivanjItems = [
              {
                label: 'Veži za eOtpremnicu/ePrijemnicu',
                command: () => this.openAddEOtpremnicaDialog(),
              },
            ];
          }
        }
      })
    );
  }

  goToStranka() {
    this._location.routeToStrankaKartica(
      this.racunInfoDto.strankaId,
      this.racunInfoDto.tipStranke
    );
  }

  private _setRazlogText() {
    switch (this.racunInfoDto.statusDto.status) {
      case StatusRacuna.OTKAZANO:
        this.razlogLabel = 'otkazivanja';
        break;
      case StatusRacuna.ODBIJENO:
        this.razlogLabel = 'odbijanja';
        break;
      case StatusRacuna.STORNIRANO:
        this.razlogLabel = 'storniranja';
        break;
      default:
        break;
    }
  }

  openAddPovezaniRacunDialog() {
    this.dialogConfig.data = { racun: this.racunInfoDto };
    this.dialogConfig.header = 'Povezivanje računa';
    this.dialogConfig.customSubmitButton = {
      label: 'Dodaj',
      icon: 'fas fa-plus'
    } as any;

    const ref = this._dialogService.open(
      AddPovezaniRacunFormComponent,
      this.dialogConfig
    );
  }

  openAddEOtpremnicaDialog() {
    this.dialogConfig.data = {
      racunId: this.racunInfoDto.id,
      tip: this.racunInfoDto.tip,
      vrsta: this.racunInfoDto.vrsta,
    };
    this.dialogConfig.header = 'Poveži eOtpremnicu/ePrijemnicu';
    this.dialogConfig.customSubmitButton = {
      label: 'Dodaj',
      icon: 'fas fa-plus'
    } as any;

    const ref = this._dialogService.open(
      AddEOtpremnicaFormComponent,
      this.dialogConfig
    );

    this._getSub.add(
      ref.onClose.subscribe((result) => {
        if (result) {
          location.reload();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._racunInfoService.setVezaniRacuni = [];
    this._getSub.unsubscribe();
  }
}
